import { TRUST_GRAPH_RISK_CLASSES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import type {
	DashboardOverviewConnection,
	DashboardOverviewLayer,
	DashboardOverviewResponse,
	TrustGraphTone,
} from '@uncain/shared';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DashboardOverviewNodeCard } from './dashboard-overview-node';

interface ConnectorPath {
	readonly id: string;
	readonly d: string;
	readonly tone: TrustGraphTone;
}

const CONNECTOR_CLASSES: Record<TrustGraphTone, string> = {
	neutral: 'text-border/80',
	info: 'text-primary/35',
	success: 'text-green-500/30',
	warning: 'text-amber-500/35',
	danger: 'text-red-500/35',
};

const LANE_GRADIENTS = [
	'from-sky-50/70 via-card to-card',
	'from-indigo-50/60 via-card to-card',
	'from-amber-50/70 via-card to-card',
	'from-emerald-50/70 via-card to-card',
	'from-rose-50/70 via-card to-card',
];

export function DashboardOverviewGraph({ overview }: { overview: DashboardOverviewResponse }) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const [paths, setPaths] = useState<readonly ConnectorPath[]>([]);

	const layers = overview.layers;
	const connections = overview.connections;

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}

		const recompute = () => {
			const containerRect = container.getBoundingClientRect();
			const nextPaths = connections
				.map((connection) => buildConnectorPath(connection, nodeRefs.current, containerRect))
				.filter((path): path is ConnectorPath => path !== null);
			setPaths(nextPaths);
		};

		const frame = requestAnimationFrame(recompute);
		const observer = new ResizeObserver(() => recompute());
		observer.observe(container);
		window.addEventListener('resize', recompute);

		return () => {
			cancelAnimationFrame(frame);
			observer.disconnect();
			window.removeEventListener('resize', recompute);
		};
	}, [connections]);

	const laneCountSummary = useMemo(
		() =>
			overview.layers.reduce(
				(acc, layer) => {
					acc.items += layer.itemCount;
					acc.findings += layer.findingCount;
					return acc;
				},
				{ items: 0, findings: 0 },
			),
		[overview.layers],
	);

	return (
		<section className="space-y-4 rounded-[1.9rem] border border-border/70 bg-card p-5 shadow-sm">
			<div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
				<div>
					<div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
						CI/CD Overview
					</div>
					<h2 className="mt-1 text-xl font-semibold text-foreground">
						Layered trust surface overview
					</h2>
					<p className="mt-1 text-sm text-muted-foreground">
						A high-level view of where trusted execution originates, accumulates risk, and reaches
						release controls.
					</p>
				</div>
				<div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
					<SummaryPill label="Layers" value={layers.length} />
					<SummaryPill label="Surface Points" value={laneCountSummary.items} />
					<SummaryPill label="Tracked Findings" value={overview.summary.findingCount} />
				</div>
			</div>

			<div
				ref={containerRef}
				className="relative overflow-hidden rounded-[1.7rem] border border-border/60 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.98),_rgba(248,250,252,0.94)_45%,_rgba(241,245,249,0.9)_100%)] p-3"
			>
				<svg
					className="pointer-events-none absolute inset-0 z-0 hidden xl:block"
					aria-hidden="true"
				>
					{paths.map((path) => (
						<path
							key={path.id}
							d={path.d}
							className={cn('fill-none stroke-[2.5] drop-shadow-sm', CONNECTOR_CLASSES[path.tone])}
							stroke="currentColor"
							strokeLinecap="round"
						/>
					))}
				</svg>

				<div className="relative z-10 grid gap-3 xl:grid-cols-5">
					{layers.map((layer, index) => (
						<DashboardOverviewLane
							key={layer.id}
							layer={layer}
							gradientClass={LANE_GRADIENTS[index] ?? LANE_GRADIENTS[0]}
							assignRef={(nodeId, element) => {
								nodeRefs.current[nodeId] = element;
							}}
						/>
					))}
				</div>
			</div>
		</section>
	);
}

function DashboardOverviewLane({
	layer,
	gradientClass,
	assignRef,
}: {
	readonly layer: DashboardOverviewLayer;
	readonly gradientClass: string;
	readonly assignRef: (nodeId: string, element: HTMLDivElement | null) => void;
}) {
	return (
		<div
			className={cn('rounded-[1.5rem] border border-border/70 bg-gradient-to-b p-4', gradientClass)}
		>
			<div className="mb-4 space-y-2">
				<div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
					{layer.label}
				</div>
				<div className="flex items-end justify-between gap-3">
					<div className="text-3xl font-semibold text-foreground">{layer.itemCount}</div>
					<div className={cn('text-xs font-medium', TRUST_GRAPH_RISK_CLASSES[layer.risk])}>
						{layer.findingCount} findings
					</div>
				</div>
			</div>

			<div className="space-y-3">
				{layer.nodes.map((node) => (
					<DashboardOverviewNodeCard
						key={node.id}
						node={node}
						assignRef={(element) => assignRef(node.id, element)}
					/>
				))}
			</div>
		</div>
	);
}

function SummaryPill({ label, value }: { readonly label: string; readonly value: number }) {
	return (
		<span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-muted/30 px-3 py-1.5">
			<span>{label}</span>
			<span className="font-semibold text-foreground">{value}</span>
		</span>
	);
}

function buildConnectorPath(
	connection: DashboardOverviewConnection,
	nodeRefs: Record<string, HTMLDivElement | null>,
	containerRect: DOMRect,
): ConnectorPath | null {
	const from = nodeRefs[connection.fromNodeId];
	const to = nodeRefs[connection.toNodeId];
	if (!(from && to)) {
		return null;
	}

	const fromRect = from.getBoundingClientRect();
	const toRect = to.getBoundingClientRect();
	const startX = fromRect.right - containerRect.left;
	const startY = fromRect.top + fromRect.height / 2 - containerRect.top;
	const endX = toRect.left - containerRect.left;
	const endY = toRect.top + toRect.height / 2 - containerRect.top;
	const deltaX = Math.max((endX - startX) / 2, 36);
	const d = `M ${startX} ${startY} C ${startX + deltaX} ${startY}, ${endX - deltaX} ${endY}, ${endX} ${endY}`;

	return { id: connection.id, d, tone: connection.tone };
}
